import { Lock } from 'lucide-react';

import { useAuth } from '@chainlit/react-client';

import RenewDialog from './RenewDialog';
import { Access, dayMonth, openRenewDialog } from './useAccess';

/**
 * The one screen an account sees once its access date has passed, in place of
 * every page. It replaces "Your account is expired", which used to appear only
 * inside the chat while the dashboard, anomalies and e-mails carried on.
 *
 * It says what happened, that nothing was lost, and what to do. Renewing is
 * an admin changing one date, and everything comes back on the next refresh.
 */
export default function AccessEnded({ access }: { access: Access }) {
  const { logout } = useAuth();
  const ended = access.last_day ? dayMonth(access.last_day) : null;

  return (
    <div className="min-h-[100dvh] w-full grid place-items-center bg-[var(--sig-panel)] px-4 py-8">
      <div className="w-full max-w-[400px] rounded-[14px] border border-[var(--sig-border)] bg-[var(--sig-surface)] px-5 py-6 text-center">
        <span className="mx-auto grid size-10 place-items-center rounded-full bg-[var(--sig-panel)] text-[var(--sig-muted)]">
          <Lock className="size-5" />
        </span>
        <h1 className="mt-3 text-[17px] font-semibold text-[var(--sig-ink)]">
          Your Signals access has ended
        </h1>
        <p className="mt-2 text-[13.5px] leading-relaxed text-[var(--sig-muted)]">
          {ended ? `Your access ran until ${ended}. ` : ''}
          Your conversations, dashboards and alerts are kept, and everything
          comes back as soon as you renew.
        </p>
        <div className="mt-5 flex flex-wrap justify-center gap-2">
          <button
            type="button"
            onClick={openRenewDialog}
            className="rounded-lg bg-[var(--sig-blue)] px-4 py-2 text-[13.5px] font-medium text-white hover:opacity-90"
          >
            {access.renewal_requested_at ? 'Request sent' : 'Request renewal'}
          </button>
          <button
            type="button"
            onClick={() => logout(true)}
            className="rounded-lg px-4 py-2 text-[13.5px] text-[var(--sig-muted)] hover:bg-[var(--sig-panel)]"
          >
            Sign out
          </button>
        </div>
      </div>
      <RenewDialog />
    </div>
  );
}
